import { useEffect, useRef, useState } from "react";
import jsQR from "jsqr";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export function QrScanner({ open, onOpenChange, onResult }) {
  const videoRef = useRef(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    let stream;
    let raf;
    let alive = true;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    const tick = () => {
      const v = videoRef.current;
      if (!alive) return;
      if (v && v.readyState >= 2 && v.videoWidth) {
        canvas.width = v.videoWidth;
        canvas.height = v.videoHeight;
        ctx.drawImage(v, 0, 0);
        const hit = jsQR(ctx.getImageData(0, 0, canvas.width, canvas.height).data, canvas.width, canvas.height, { inversionAttempts: "attemptBoth" });
        if (hit?.data) { onResult(hit.data); onOpenChange(false); return; }
      }
      raf = requestAnimationFrame(tick);
    };
    setError("");
    if (!navigator.mediaDevices?.getUserMedia) { setError("Camera unavailable — paste the key instead"); return; }
    navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: false })
      .then((s) => {
        if (!alive) { s.getTracks().forEach((t) => t.stop()); return; }
        stream = s;
        if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.play().catch(() => {}); }
        raf = requestAnimationFrame(tick);
      })
      .catch(() => setError("Camera permission denied — paste the key instead"));
    return () => { alive = false; cancelAnimationFrame(raf); stream?.getTracks().forEach((t) => t.stop()); };
  }, [open, onOpenChange, onResult]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="qr-scanner-dialog" className="bg-nebula border-rim text-ash sm:max-w-sm rounded-secure w-[calc(100%-2rem)]">
        <DialogHeader>
          <DialogTitle className="font-branding">Scan login key</DialogTitle>
          <DialogDescription className="text-ash/50">Point the camera at the QR code in Settings on your other device.</DialogDescription>
        </DialogHeader>
        <div className="relative aspect-square bg-void rounded-secure overflow-hidden border border-stellar/30">
          <video ref={videoRef} playsInline muted className="w-full h-full object-cover" data-testid="qr-scanner-video" />
          <div className="absolute inset-8 border-2 border-stellar/60 rounded-secure pointer-events-none" />
        </div>
        {error && <p className="text-xs text-flare" data-testid="qr-scanner-error">{error}</p>}
      </DialogContent>
    </Dialog>
  );
}
